import { formataRotasGlobais } from '@/shared/common/functions/roteamento'

/**
 * Rotas de módulos OPCIONAIS que valem dentro de qualquer módulo: `/crm/importacao`,
 * `/pedidos/vendas/importacao`... — como as globais (`/crm/usuarios`), só que sem import fixo.
 *
 * O módulo opcional é achado no build por `require.context` sobre `src/modules` (pasta que
 * sempre existe), filtrando o caminho exato do `routes.js` dele. Sem a pasta do módulo, a
 * busca volta vazia e nada é registrado: o build não quebra e o endereço cai no "não
 * encontrada" de sempre. As telas continuam carregadas sob demanda (`() => import`).
 *
 * Registra na primeira navegação, não ao carregar o arquivo: aí o router do módulo já
 * adicionou as rotas dele, e um endereço que o módulo já tenha não é sobrescrito.
 *
 * Para acrescentar outro módulo opcional: uma entrada em OPCIONAIS (o `routes.js` dele tem
 * as mesmas regras de `formataRotasGlobais`: sem `name`, sem `path: '/'`).
 */
const OPCIONAIS = [
    {
        // Pasta e prefixo do módulo dono (`/importacao`); no próprio pacote dele não registra.
        modulo: 'importacao',
        rotas: require.context('@/modules', true, /^\.\/importacao\/domains\/importacao\/router\/routes\.js$/)
    }
]

const modulosLocais = () => {
    try {
        const lista = require('@/modules/modules.json')
        return Array.isArray(lista) ? lista : []
    } catch (erro) {
        return []
    }
}

/**
 * Prefixos do pacote carregado: o módulo da página (primeiro segmento do endereço, que é o
 * `<modulo>/index.html` que o servidor entregou) e os submódulos dele, que rodam no mesmo
 * pacote. No core (`/clientes`, `/inicio`) não há módulo e a lista sai vazia.
 */
const prefixosDoPacote = (caminho) => {
    const modulos = modulosLocais()
    const primeiro = String(caminho || '').split('?')[0].split('/')[1] || ''
    if (!primeiro || !modulos.includes(primeiro)) {
        return { modulo: null, prefixos: [] }
    }
    const submodulos = modulos
        .filter((item) => item.indexOf(primeiro + ':') === 0)
        .map((item) => primeiro + '/' + item.split(':')[1])
    return { modulo: primeiro, prefixos: [primeiro].concat(submodulos) }
}

const rotasDoOpcional = (contexto) => {
    const lista = []
    contexto.keys().forEach((chave) => {
        const rotas = contexto(chave).default
        if (Array.isArray(rotas)) {
            lista.push(...rotas)
        }
    })
    return lista
}

// O endereço já é de alguma rota de verdade (não só do "não encontrada" `*`)?
const jaExiste = (router, caminho) => {
    const { route } = router.resolve(caminho)
    return route.matched.length > 0 && !route.matched.some((rota) => rota.path === '*')
}

/** Registra as rotas opcionais no pacote atual. Devolve true se registrou alguma. */
export const registraRotasOpcionais = (router, caminho) => {
    let registrou = false
    try {
        const { modulo, prefixos } = prefixosDoPacote(caminho)
        if (!modulo) {
            return false
        }
        OPCIONAIS.forEach((opcional) => {
            if (opcional.modulo === modulo) {
                return
            }
            const rotas = rotasDoOpcional(opcional.rotas)
            if (!rotas.length) {
                return
            }
            prefixos.forEach((prefixo) => {
                const base = '/' + prefixo + '/' + opcional.modulo
                if (jaExiste(router, base)) {
                    return
                }
                router.addRoutes([{
                    path: base,
                    component: () => import('@/shared/components/layout/Interno'),
                    children: formataRotasGlobais(prefixo, rotas)
                }])
                registrou = true
            })
        })
    } catch (erro) {
        // Falha aqui não pode derrubar o módulo: fica sem as rotas opcionais.
        console.error('[rotasOpcionais]', erro)
    }
    return registrou
}

/**
 * Guarda que registra na primeira navegação. Se o endereço de entrada só casava com o `*`
 * e agora casa com uma rota opcional, refaz a navegação para ela.
 */
export const guardaRotasOpcionais = (router) => {
    let feito = false
    return (to, from, next) => {
        if (feito) {
            return next()
        }
        feito = true
        const soNaoEncontrada = to.matched.some((rota) => rota.path === '*')
        if (registraRotasOpcionais(router, to.fullPath) && soNaoEncontrada && jaExiste(router, to.fullPath)) {
            return next(to.fullPath)
        }
        return next()
    }
}
